import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button, Card, Field } from "@/components/ui";
import { useToast } from "@/contexts";
import { checkOrgSlug, createOrg, getCurrentOrg } from "@/features/organizations/api";

export default function OrgCreate() {
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [creating, setCreating] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await getCurrentOrg();
      if (data) {
        navigate({ to: "/org", replace: true });
        return;
      }
      setCheckingExisting(false);
    })();
  }, [navigate]);

  useEffect(() => {
    if (slug.length < 2) {
      setSlugAvailable(null);
      return;
    }
    setSlugChecking(true);
    const timer = setTimeout(async () => {
      const { data } = await checkOrgSlug(slug);
      if (data) setSlugAvailable(data.available);
      setSlugChecking(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [slug]);

  function handleSlugChange(value: string) {
    setSlug(value.toLowerCase().replace(/[^a-z0-9_-]/g, ""));
  }

  async function handleCreate() {
    if (!name.trim()) {
      toast.showAlert("Name required", "Enter an organization name.", "error");
      return;
    }
    if (!slug.trim() || slug.length < 2) {
      toast.showAlert("Slug required", "Enter a slug with at least 2 characters.", "error");
      return;
    }
    if (slugAvailable === false) {
      toast.showAlert("Slug taken", "That slug is already in use.", "error");
      return;
    }

    setCreating(true);
    const { data, error } = await createOrg({ name: name.trim(), slug: slug.trim() });
    if (error) {
      toast.showAlert("Create failed", error.message, "error");
      setCreating(false);
    } else if (data) {
      toast.showAlert("Created", `Organization "${name.trim()}" created!`, "success");
      navigate({ to: "/org", replace: true });
    }
  }

  if (checkingExisting) return null;

  return (
    <div className="min-h-screen bg-background relative">
      <div className="h-screen overflow-y-auto">
        <div className="flex flex-col px-5 pt-3 pb-9 gap-5 md:px-5 max-w-3xl mx-auto w-full">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate({ to: "/dashboard" })}
              className="py-1.5 px-2.5 border border-[rgba(28,27,46,0.1)] rounded-[16px] bg-[rgba(255,255,255,0.4)] cursor-pointer"
            >
              <span className="text-text-muted text-[0.8125rem] font-bold">&larr; Back</span>
            </button>
            <span className="text-text text-[1.75rem] font-black tracking-[-0.0313em]">
              Create Organization
            </span>
          </div>

          <Card tone="strong">
            <Field label="Organization name" value={name} onChangeText={setName} />
            <div className="flex flex-col gap-2">
              <label
                htmlFor="org-slug"
                className="text-text-muted text-[0.625rem] font-bold uppercase tracking-[0.05em]"
              >
                Organization slug
              </label>
              <div className="relative">
                <input
                  id="org-slug"
                  type="text"
                  value={slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="my-org"
                  className="w-full bg-white rounded-2xl border border-text px-3.5 py-3 text-text text-sm font-inherit"
                />
                {slugChecking && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-dim text-xs">
                    Checking...
                  </span>
                )}
                {slugAvailable === true && !slugChecking && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-aqua text-xs font-bold">
                    Available
                  </span>
                )}
                {slugAvailable === false && !slugChecking && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#cc0000] text-xs font-bold">
                    Taken
                  </span>
                )}
              </div>
              <p className="text-text-muted text-xs">
                This will be used in URLs: /org/{slug || "my-org"}
              </p>
            </div>
          </Card>

          <Button
            label={creating ? "Creating..." : "Create organization"}
            onPress={handleCreate}
            kind={
              creating || !name.trim() || slug.length < 2 || slugAvailable === false
                ? "ghost"
                : "primary"
            }
          />
        </div>
      </div>
    </div>
  );
}
